import React, { useEffect, useMemo, useState } from "react";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "../../firebase";
import { useAuth } from "../AuthProvider";
import { useVenue } from "../VenueProvider";
import ConceptoPicker from "./ConceptoPicker";
import { loadProveedorCatalogs } from "../../utils/catalogs";
import { Switch } from "../switch";
import { showAlert } from "../../alertService";
import { logBitacora, BITACORA_ACTIONS } from "../../utils/bitacora";
import { fmtMXNInput, normalizeMoneyInput } from "../../utils/money";
import {
  totalDesglose,
  lineaDescuentoPorcentaje,
  lineaRedondeo,
} from "../../utils/valorEvento";
import {
  esDescuento,
  esItemizado,
  esLineaIncluida,
  esPool,
  esRenta,
  piezasReservablesDe,
  cantidadRealDeLinea,
  grupoDeRenta,
  incluyeDe,
  lineaDesdeItem,
  lineaAjustada,
  lineasIncluidasDe,
  proveedorDe,
  unidadDe,
  variantesDe,
  varianteDe,
  precioDe,
  precioDeVariante,
  costoProveedorDe,
} from "../../utils/catalogo";
import { agregarVariante } from "../../utils/servicios";
import ServicioCreateDialog from "../servicios/ServicioCreateDialog";
import { PreviewVariante } from "../servicios/ServicioForm";
import {
  PencilSquareIcon,
} from "@heroicons/react/20/solid";

const ACCENT = "#1b4d2e";

const inputClass =
  "w-full rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]";

const fmtMoney = (v, dec = null) => {
  const n = Number(v) || 0;
  const opts =
    dec == null
      ? { minimumFractionDigits: n % 1 !== 0 ? 2 : 0, maximumFractionDigits: 2 }
      : { minimumFractionDigits: dec, maximumFractionDigits: dec };
  return (n < 0 ? "-$" : "$") + Math.abs(n).toLocaleString("es-MX", opts);
};

const plural = (n, unidad) => (n === 1 || /s$/i.test(unidad) ? unidad : `${unidad}s`);

// Valores fijos del selector de concepto que no son renglones del catálogo.
const OPCION_DESCUENTO = "__descuento__";
const OPCION_LIBRE = "__libre__";

const FORM_VACIO = {
  modo: "", // "" | servicio | descuento | libre
  conceptoId: "",
  varianteId: "",
  cantidad: "1",
  concept: "",
  precioManual: "",
  ajustarPrecio: false,
  descuentoModo: "monto", // monto | pct
  descuentoPct: "",
  // Solo cuando el concepto elegido es una renta: conceptoId → {sel, cantidad}
  // de lo que trae. Llega todo marcado con la cantidad configurada y el owner
  // desmarca lo que este evento no lleve.
  incluidos: {},
  // El grupo que se está editando, para reemplazar la renta y sus incluidos
  // juntos en vez de dejar hijos huérfanos.
  grupoId: null,
};

// Lo que trae la renta, listo para el formulario: todo seleccionado con su
// cantidad. `previos` son las líneas que ya existen en el desglose cuando se
// edita un grupo — manda lo que quedó guardado, no lo que el catálogo dice
// hoy, porque el owner ya decidió una vez para este evento.
const incluidosDeRenta = (item, conceptosPorId, previos = null) => {
  const out = {};
  incluyeDe(item).forEach(({ conceptoId, cantidad }) => {
    if (!conceptosPorId[conceptoId]) return;
    out[conceptoId] = { sel: !previos, cantidad: String(cantidad) };
  });
  (previos || []).forEach((l) => {
    if (!l?.conceptoId) return;
    out[l.conceptoId] = { sel: true, cantidad: String(cantidadRealDeLinea(l) || 1) };
  });
  return out;
};

const NUEVA_VARIANTE_VACIA = { nombre: "", precio: "", costoProveedor: "" };

// Quita lo que Firestore no acepta dentro de un array (undefined).
const limpia = (obj) =>
  Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined));

// El cotizador (FRA-352). Lo comparten EventPage y LeadPage, que antes
// tenían el mismo cotizador duplicado. La línea guarda la CANTIDAD REAL
// (13 horas, 80 personas) y el precio sale del servicio —o de su variante—:
// un número por unidad, multiplicado por la cantidad. Cuando se negoció
// distinto, el precio se ajusta a mano en la línea (`lineaAjustada`). Una
// variante que no existe se crea desde aquí y queda en el catálogo.
//
// Lectura tolerante: las cotizaciones existentes traen líneas planas
// (units × unitPrice) y se siguen leyendo y editando igual — es la misma
// forma que escriben las líneas nuevas.
//
// El respaldo de cada línea (para el margen) se deriva del servicio elegido
// vía `conceptoId`; no hay control manual "Sale de".
export default function CotizadorDesglose({
  venueId,
  desglose = [],
  ivaEnabled = false,
  onIvaChange = null,
  onDesgloseChange,
  conceptos = [],
  // El catálogo SIN filtrar. `conceptos` son los cotizables, que es lo que
  // debe ver el picker; pero lo que una renta incluye casi siempre NO es
  // cotizable —la limpieza de baños existe solo para ir dentro— y si se
  // resolviera contra la lista filtrada desaparecería de la renta en silencio.
  // Sin la prop se cae a `conceptos`, que es lo que hay.
  conceptosTodos = null,
  conceptosLoading = false,
  readOnly: readOnlyProp = false,
  // Solo lectura por rol (inversionista, P-FRA-13). Hace lo mismo que
  // `readOnly` —que ya apaga editar, quitar, redondear y el alta de líneas—;
  // vive aparte para que el llamador no mezcle "lead convertido" con "rol".
  soloLectura = false,
  precioAcordado = null, // number | null: si viene, se compara contra el total
  puedeCrearServicio = false,
  titulo = "Desglose",
}) {
  const readOnly = readOnlyProp || soloLectura;
  const { user, userInfo } = useAuth();
  const { proveedores, selectedVenueId } = useVenue();

  // El directorio solo sirve para agrupar si es el del MISMO venue: un admin
  // abriendo el evento de otro venue vería categorías que no son. Sin él, el
  // picker degrada a agrupar por el `proveedorNombre` que cada servicio ya
  // guarda, que para esto alcanza.
  const proveedoresDelPicker = venueId === selectedVenueId ? proveedores || [] : [];

  // Catálogo compartido de categorías de proveedor: es la ÚNICA jerarquía
  // que existe hoy, porque los servicios no tienen categoría propia.
  const [categoriaNombrePorId, setCategoriaNombrePorId] = useState({});
  useEffect(() => {
    let vivo = true;
    loadProveedorCatalogs()
      .then(({ categorias }) => {
        if (!vivo) return;
        const m = {};
        (categorias || []).forEach((c) => { m[c.id] = c.nombre; });
        setCategoriaNombrePorId(m);
      })
      // Sin catálogo el picker agrupa por proveedor y ya: no es motivo para
      // romper el cotizador.
      .catch((err) => console.error("Catálogo de categorías:", err));
    return () => { vivo = false; };
  }, []);
  const [form, setForm] = useState(FORM_VACIO);
  const [editandoIdx, setEditandoIdx] = useState(null);
  const [nuevaVariante, setNuevaVariante] = useState(null); // null | {nombre, precio, costoProveedor}
  const [guardandoVariante, setGuardandoVariante] = useState(false);
  const [showCrearServicio, setShowCrearServicio] = useState(false);
  // Variantes creadas al vuelo que el snapshot del catálogo todavía no
  // trae (o nunca traerá, si el evento es de otro venue).
  const [variantesExtra, setVariantesExtra] = useState({}); // conceptoId → [variante]

  const lineas = Array.isArray(desglose) ? desglose : [];
  const subtotal = lineas.reduce(
    (s, l) => s + (Number(l.units) * Number(l.unitPrice) || 0),
    0
  );
  const ivaAmount = subtotal * 0.16;
  const totalConIva = subtotal * 1.16;
  const totalFinal = ivaEnabled ? totalConIva : subtotal;

  const conceptosPorId = useMemo(() => {
    const map = {};
    (conceptosTodos || conceptos || []).forEach((c) => {
      if (!c?.id) return;
      const extra = variantesExtra[c.id];
      map[c.id] = extra?.length ? { ...c, variantes: [...variantesDe(c), ...extra] } : c;
    });
    return map;
  }, [conceptos, conceptosTodos, variantesExtra]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const reset = () => {
    setForm(FORM_VACIO);
    setEditandoIdx(null);
    setNuevaVariante(null);
  };

  const item = form.conceptoId ? conceptosPorId[form.conceptoId] || null : null;
  const itemEsDescuento = !!item && esDescuento(item);
  const itemEsRenta = !!item && esRenta(item);
  // Lo que la renta trae, en el orden configurado, con el servicio resuelto.
  // Al editar, lo que este evento ya tenía cotizado dentro de la renta se
  // suma a la lista aunque la renta ya no lo traiga en el catálogo: si no, al
  // guardar un cambio de precio esa línea desaparecía en silencio del
  // desglose (y su costo de proveedor con ella).
  const incluidosDelItem = useMemo(() => {
    if (!itemEsRenta) return [];
    const configurados = incluyeDe(item);
    const vistos = new Set(configurados.map((x) => x.conceptoId));
    const previos = Object.entries(form.incluidos || {})
      .filter(([conceptoId, v]) => v?.sel && !vistos.has(conceptoId))
      .map(([conceptoId, v]) => ({ conceptoId, cantidad: Number(v.cantidad) || 1 }));
    return [...configurados, ...previos]
      .map(({ conceptoId, cantidad }) => ({
        concepto: conceptosPorId[conceptoId] || null,
        conceptoId,
        cantidadConfigurada: cantidad,
      }))
      .filter((x) => x.concepto);
  }, [item, itemEsRenta, conceptosPorId, form.incluidos]);
  const incluidosMarcados = useMemo(
    () => incluidosDelItem.filter((x) => form.incluidos?.[x.conceptoId]?.sel),
    [incluidosDelItem, form.incluidos]
  );

  // ── Cuántas piezas EXISTEN del servicio elegido ──
  // Cotizar 80 calentadores cuando tienes 6 no es un detalle de disponibilidad
  // por fecha: es una promesa que no se puede cumplir ningún día. El tope sale
  // del catálogo (modo por cantidad) o de contar las piezas activas (modo
  // identificadas), y por eso solo en el segundo caso hace falta una
  // suscripción — que se abre nada más al elegir un servicio con piezas.
  const itemItemizado = !!item && esItemizado(item);
  const itemEsPool = !!item && esPool(item);
  const [piezasActivas, setPiezasActivas] = useState(null);
  useEffect(() => {
    setPiezasActivas(null);
    if (!venueId || !item?.id || !itemItemizado || itemEsPool) return;
    const unsub = onSnapshot(
      collection(db, "venues", venueId, "conceptos", item.id, "unidades"),
      (snap) =>
        setPiezasActivas(
          snap.docs.filter((d) => (d.data().estado || "activo") === "activo").length
        ),
      (err) => console.error("Unidades del picker:", err)
    );
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venueId, item?.id, itemItemizado, itemEsPool]);

  const variantes = item ? variantesDe(item) : [];
  const variante = item ? varianteDe(item, form.varianteId) : null;
  const unidad = item ? unidadDe(item) : "unidad";
  const cantidad = Number(form.cantidad) > 0 ? Number(form.cantidad) : 0;
  // null = no aplica o todavía no se sabe; nunca se bloquea con null. Tampoco
  // con 0: un servicio con piezas cuyas piezas aún no se dan de alta (los
  // docs viejos, o un alta a medias) tiene que seguir siendo cotizable. Y la
  // comparación solo tiene sentido si la cantidad cotizada SON piezas: 8 horas
  // de 6 calentadores no exceden nada.
  const capacidadItem = !itemItemizado
    ? null
    : itemEsPool
    ? piezasReservablesDe(item)
    : piezasActivas;
  const excedeCapacidad =
    capacidadItem != null &&
    capacidadItem > 0 &&
    item?.unidad === "pieza" &&
    cantidad > capacidadItem;

  const precioActivo = item ? precioDeVariante(item, variante) : 0;
  // Lo que la variante HEREDA si el campo se deja vacío (utils/catalogo.js:
  // null = hereda). Se calcula aquí para poder enseñarlo en el formulario.
  const precioBaseItem = item ? precioDe(item) : 0;
  const costoBaseItem = item ? costoProveedorDe(item) : 0;

  const margenVarianteNueva = useMemo(() => {
    if (!nuevaVariante || !item) return null;
    const precioCapturado = Number(normalizeMoneyInput(nuevaVariante.precio)) || 0;
    const costoCapturado = Number(normalizeMoneyInput(nuevaVariante.costoProveedor)) || 0;
    const precio = precioCapturado > 0 ? precioCapturado : precioBaseItem;
    const costo = costoCapturado > 0 ? costoCapturado : costoBaseItem;
    // Sin costo no hay margen que enseñar: cobrar algo con costo cero no es
    // información, es ruido (mismo criterio que utils/costos.js).
    if (!(precio > 0) || !(costo > 0)) return null;
    return {
      precio,
      costo,
      costoHeredado: !(costoCapturado > 0),
      margen: precio - costo,
      pct: precio > 0 ? (precio - costo) / precio : null,
    };
  }, [nuevaVariante, item, precioBaseItem, costoBaseItem]);

  const proveedorDelItem = item?.proveedorId
    ? (proveedores || []).find((p) => p.id === item.proveedorId) || null
    : null;

  // Vista previa de la línea que se va a registrar.
  const preview = useMemo(() => {
    if (form.modo !== "servicio" || !item || itemEsDescuento || !(cantidad > 0)) return null;
    const precio = form.ajustarPrecio
      ? Number(normalizeMoneyInput(form.precioManual)) || 0
      : precioActivo;
    return { total: cantidad * precio, ajustado: !!form.ajustarPrecio };
  }, [form.modo, form.ajustarPrecio, form.precioManual, item, itemEsDescuento, cantidad, precioActivo]);

  const persist = (nuevo) => {
    onDesgloseChange?.(nuevo);
  };

  // ── Selección de concepto ──
  const valorListbox =
    form.modo === "descuento" && !form.conceptoId
      ? OPCION_DESCUENTO
      : form.modo === "libre"
      ? OPCION_LIBRE
      : form.conceptoId || "";

  const elegirConcepto = (val) => {
    setNuevaVariante(null);
    if (val === OPCION_DESCUENTO) {
      set({
        modo: "descuento",
        conceptoId: "",
        varianteId: "",
        concept: "Descuento",
        precioManual: "",
        descuentoModo: "monto",
        descuentoPct: "",
        cantidad: "1",
        ajustarPrecio: false,
      });
      return;
    }
    if (val === OPCION_LIBRE) {
      set({
        modo: "libre",
        conceptoId: "",
        varianteId: "",
        concept: "",
        precioManual: "",
        cantidad: "1",
        ajustarPrecio: false,
      });
      return;
    }
    const c = conceptosPorId[val];
    if (!c) return;
    if (esDescuento(c)) {
      const plano = precioDe(c);
      set({
        modo: "descuento",
        conceptoId: c.id,
        varianteId: "",
        concept: c.nombre || "Descuento",
        precioManual: plano ? String(Math.abs(plano)) : "",
        descuentoModo: "monto",
        descuentoPct: "",
        cantidad: "1",
        ajustarPrecio: false,
      });
      return;
    }
    set({
      modo: "servicio",
      conceptoId: c.id,
      varianteId: "",
      concept: c.nombre || "",
      precioManual: "",
      cantidad: form.cantidad || "1",
      ajustarPrecio: false,
      // Una renta llega con todo lo que trae ya marcado: el caso normal es que
      // se dé completo, y desmarcar es la excepción.
      incluidos: esRenta(c) ? incluidosDeRenta(c, conceptosPorId) : {},
      grupoId: form.grupoId,
    });
  };

  const marcarIncluido = (conceptoId, sel) =>
    setForm((f) => ({
      ...f,
      incluidos: {
        ...f.incluidos,
        [conceptoId]: { ...(f.incluidos?.[conceptoId] || { cantidad: "1" }), sel },
      },
    }));

  const cantidadIncluido = (conceptoId, cantidad) =>
    setForm((f) => ({
      ...f,
      incluidos: {
        ...f.incluidos,
        [conceptoId]: { ...(f.incluidos?.[conceptoId] || { sel: true }), cantidad },
      },
    }));

  const marcarTodos = (sel) =>
    setForm((f) => ({
      ...f,
      incluidos: Object.fromEntries(
        incluidosDelItem.map((x) => [
          x.conceptoId,
          {
            sel,
            cantidad: f.incluidos?.[x.conceptoId]?.cantidad ?? String(x.cantidadConfigurada),
          },
        ])
      ),
    }));

  // ── Armar y guardar la línea ──
  // Devuelve un ARRAY: una renta entra como varias líneas —ella y cada
  // incluido— y todo lo demás como una sola. Así el desglose, el PDF y el
  // puente de costos ven renglones normales en los dos casos.
  const armarLineas = () => {
    if (form.modo === "servicio") {
      if (!item) {
        showAlert("Elige un servicio.", "error");
        return null;
      }
      if (!(cantidad > 0)) {
        showAlert(`Ingresa cuántas ${plural(2, unidad)} se cotizan.`, "error");
        return null;
      }
      if (form.varianteId === "__nueva__") {
        showAlert("Guarda la variante nueva antes de registrar la línea.", "error");
        return null;
      }
      // Prometer 80 calentadores cuando tienes 6 no se corrige después: la
      // cotización ya salió. Se bloquea aquí, no se avisa y ya.
      if (excedeCapacidad) {
        showAlert(
          `Solo tienes ${capacidadItem} ${plural(capacidadItem, unidad)} de ${item.nombre}` +
            ` y estás cotizando ${cantidad}. Baja la cantidad, o si consigues más` +
            ` prestadas, cotízalas como un servicio aparte.`,
          "error"
        );
        return null;
      }
      let base = null;
      if (form.ajustarPrecio) {
        const precio = Number(normalizeMoneyInput(form.precioManual));
        if (isNaN(precio)) {
          showAlert("Ingresa un precio válido.", "error");
          return null;
        }
        base = limpia(
          lineaAjustada(item, { unidades: cantidad, varianteId: form.varianteId || null, unitPrice: precio })
        );
      } else {
        if (!(precioActivo > 0)) {
          showAlert(
            "Este servicio no tiene precio al cliente. Ajusta el precio a mano o captúralo en Servicios.",
            "error"
          );
          return null;
        }
        base = limpia(lineaDesdeItem(item, { unidades: cantidad, varianteId: form.varianteId || null }));
      }
      if (!itemEsRenta) return [base];
      const incluidos = incluidosMarcados.map((x) => ({
        item: x.concepto,
        cantidad: Number(form.incluidos?.[x.conceptoId]?.cantidad) || x.cantidadConfigurada,
      }));
      return grupoDeRenta(base, incluidos, { grupoId: form.grupoId }).map(limpia);
    }
    if (form.modo === "descuento") {
      const nombre = String(form.concept || "").trim() || "Descuento";
      if (form.descuentoModo === "pct") {
        const pct = Number(form.descuentoPct);
        if (!(pct > 0) || pct > 100) {
          showAlert("Ingresa un porcentaje entre 0 y 100.", "error");
          return null;
        }
        // Se calcula sobre el desglose SIN la línea que se está editando.
        const base = editandoIdx == null ? lineas : lineas.filter((_, i) => i !== editandoIdx);
        const linea = lineaDescuentoPorcentaje({ desglose: base, pct, nombre });
        if (!linea) {
          showAlert(
            "Agrega primero los conceptos a descontar: el porcentaje se calcula sobre el total actual.",
            "error"
          );
          return null;
        }
        return [limpia({ ...linea, conceptoId: form.conceptoId || undefined })];
      }
      const monto = Number(normalizeMoneyInput(form.precioManual));
      if (!(monto > 0)) {
        showAlert("Ingresa el monto a descontar.", "error");
        return null;
      }
      return [
        limpia({
          concept: nombre,
          units: 1,
          unitPrice: -Math.abs(monto),
          conceptoId: form.conceptoId || undefined,
        }),
      ];
    }
    if (form.modo === "libre") {
      const nombre = String(form.concept || "").trim();
      if (!nombre) {
        showAlert("Ingresa el nombre del concepto.", "error");
        return null;
      }
      if (!(cantidad > 0)) {
        showAlert("Ingresa una cantidad mayor a 0.", "error");
        return null;
      }
      const precio = Number(normalizeMoneyInput(form.precioManual));
      if (isNaN(precio) || form.precioManual === "") {
        showAlert("Ingresa un precio unitario válido (puede ser negativo para descuento).", "error");
        return null;
      }
      return [{ concept: nombre, units: cantidad, unitPrice: precio }];
    }
    showAlert("Elige qué concepto agregar.", "error");
    return null;
  };

  const handleRegistrar = () => {
    const nuevas = armarLineas();
    if (!nuevas || nuevas.length === 0) return;
    let nuevo;
    if (editandoIdx == null) {
      nuevo = [...lineas, ...nuevas];
    } else {
      // Editar una renta reemplaza el grupo entero: la renta en su lugar y sus
      // incluidos justo detrás. Sin esto, desmarcar un incluido dejaba su
      // línea viva y el cliente seguía viéndola en la cotización.
      const vieja = lineas[editandoIdx];
      const grupo = vieja?.grupoId || null;
      nuevo = [];
      lineas.forEach((l, i) => {
        if (i === editandoIdx) {
          nuevo.push(...nuevas);
          return;
        }
        if (grupo && l?.incluidoEn === grupo) return;
        nuevo.push(l);
      });
    }
    persist(nuevo);
    reset();
  };

  // Quitar una renta se lleva lo que traía: un incluido sin su renta no se
  // podría ni leer ni cobrar.
  const handleQuitar = (idx) => {
    const l = lineas[idx];
    const grupo = l?.grupoId || null;
    persist(
      lineas.filter((x, i) => i !== idx && !(grupo && x?.incluidoEn === grupo))
    );
    if (editandoIdx === idx) reset();
  };

  // Editar una línea existente: si sigue ligada a un servicio del catálogo
  // se re-liga (y toma el precio vigente); si no, entra como concepto libre
  // con lo que tenía.
  const handleEditar = (idx) => {
    const l = lineas[idx];
    if (!l) return;
    setNuevaVariante(null);
    setEditandoIdx(idx);
    const c = l.conceptoId ? conceptosPorId[l.conceptoId] : null;
    if (c && esDescuento(c)) {
      setForm({
        ...FORM_VACIO,
        modo: "descuento",
        conceptoId: c.id,
        concept: l.concept || c.nombre || "Descuento",
        precioManual: String(Math.abs(Number(l.units) * Number(l.unitPrice) || 0)),
      });
      return;
    }
    if (c) {
      setForm({
        ...FORM_VACIO,
        modo: "servicio",
        conceptoId: c.id,
        varianteId: l.varianteId || "",
        concept: c.nombre || "",
        cantidad: String(cantidadRealDeLinea(l) || 1),
        ajustarPrecio: !!l.precioAjustado,
        precioManual: l.precioAjustado ? String(l.unitPrice ?? "") : "",
        // Manda lo que quedó guardado para ESTE evento, no lo que el catálogo
        // diga hoy: el owner ya decidió una vez qué entró y qué no. Lo que se
        // haya agregado después a la renta aparece desmarcado, para que se
        // pueda sumar sin resucitar lo que se quitó.
        incluidos: esRenta(c)
          ? incluidosDeRenta(c, conceptosPorId, lineasIncluidasDe(lineas, l.grupoId))
          : {},
        grupoId: l.grupoId || null,
      });
      return;
    }
    if (Number(l.unitPrice) < 0) {
      setForm({
        ...FORM_VACIO,
        modo: "descuento",
        concept: l.concept || "Descuento",
        precioManual: String(Math.abs(Number(l.units) * Number(l.unitPrice) || 0)),
      });
      return;
    }
    setForm({
      ...FORM_VACIO,
      modo: "libre",
      concept: l.concept || "",
      cantidad: String(l.units ?? 1),
      precioManual: String(l.unitPrice ?? ""),
    });
  };

  const handleRedondear = (direccion) => {
    const linea = lineaRedondeo({ desglose: lineas, ivaEnabled, direccion });
    if (!linea) return;
    persist([...lineas, linea]);
  };

  // ── Variante al vuelo ──
  const handleGuardarVariante = async () => {
    if (!item || !nuevaVariante) return;
    setGuardandoVariante(true);
    try {
      const v = await agregarVariante({
        venueId,
        item,
        nombre: nuevaVariante.nombre,
        precio: nuevaVariante.precio,
        costoProveedor: nuevaVariante.costoProveedor,
        user,
      });
      setVariantesExtra((prev) => ({
        ...prev,
        [item.id]: [...(prev[item.id] || []), v],
      }));
      logBitacora(
        venueId,
        {
          action: BITACORA_ACTIONS.SERVICIO_VARIANTE_CREATE,
          entity: "servicio",
          entityId: item.id,
          entityName: item.nombre || "",
          details: { varianteId: v.id, varianteNombre: v.nombre, desdeCotizacion: true },
        },
        userInfo,
        user
      );
      setNuevaVariante(null);
      set({ varianteId: v.id });
      showAlert(`Variante “${v.nombre}” guardada en el catálogo`, "success");
    } catch (err) {
      showAlert(err.message || "No se pudo crear la variante", "error");
    } finally {
      setGuardandoVariante(false);
    }
  };


  // ── Render ──

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm font-semibold text-zinc-700 dark:text-zinc-300">{titulo}</div>
        {onIvaChange && !readOnly && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-zinc-500 dark:text-zinc-400">Incluir IVA (16%)</span>
            <Switch color="emerald" checked={!!ivaEnabled} onChange={(val) => onIvaChange(val)} />
          </div>
        )}
      </div>

      {lineas.length === 0 ? (
        <p className="text-sm text-zinc-400 italic mb-3">No hay desglose registrado.</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-zinc-200 dark:border-zinc-700/60 mb-4">
          <table className="min-w-full text-sm">
            <thead>
              <tr style={{ backgroundColor: ACCENT + "0d" }}>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">
                  Concepto
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">
                  Cantidad
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">
                  Precio unitario
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">
                  Subtotal
                </th>
                {!readOnly && <th className="px-3 py-2" />}
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100 dark:divide-zinc-700/40">
              {lineas.map((l, idx) => {
                const c = l.conceptoId ? conceptosPorId[l.conceptoId] : null;
                const uni = c ? unidadDe(c) : null;
                const cant = cantidadRealDeLinea(l);
                const editando = editandoIdx === idx;
                // Un incluido de una renta: renglón propio —así lo lee el
                // cliente— pero sangrado y con "Incluido" donde va el precio.
                const incluida = esLineaIncluida(l);
                const editandoSuGrupo =
                  incluida &&
                  editandoIdx != null &&
                  lineas[editandoIdx]?.grupoId === l.incluidoEn;
                return (
                  <React.Fragment key={idx}>
                    <tr
                      className={
                        editando || editandoSuGrupo ? "bg-amber-50/60 dark:bg-amber-500/5" : ""
                      }
                    >
                      <td
                        className={`px-3 py-2 ${
                          incluida ? "pl-8 text-zinc-600 dark:text-zinc-400" : "text-zinc-900 dark:text-white"
                        }`}
                      >
                        <div className="flex items-start gap-1.5">
                          <span>
                            {incluida && <span className="text-zinc-300 mr-1">·</span>}
                            {l.concept || "-"}
                            {l.precioAjustado && (
                              <span className="ml-1.5 text-[10px] uppercase tracking-wide text-amber-600">
                                precio ajustado
                              </span>
                            )}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-zinc-700 dark:text-zinc-300 whitespace-nowrap">
                        {uni && Number(l.units) === cant
                          ? `${Number(l.units).toLocaleString("es-MX", { maximumFractionDigits: 2 })} ${plural(Number(l.units), uni)}`
                          : l.units}
                      </td>
                      <td className="px-3 py-2 text-zinc-700 dark:text-zinc-300 whitespace-nowrap">
                        {incluida ? (
                          <span className="text-xs font-medium uppercase tracking-wide text-zinc-400">
                            Incluido
                          </span>
                        ) : (
                          fmtMoney(l.unitPrice)
                        )}
                      </td>
                      <td className="px-3 py-2 font-medium text-zinc-900 dark:text-white whitespace-nowrap">
                        {incluida ? (
                          <span className="text-zinc-300">—</span>
                        ) : (
                          fmtMoney(Number(l.units) * Number(l.unitPrice) || 0)
                        )}
                      </td>
                      {!readOnly && (
                        <td className="px-3 py-2 whitespace-nowrap">
                          <span className="inline-flex items-center gap-2">
                            {/* Un incluido no se edita solo: su cantidad y si
                                va o no se deciden en la renta que lo trae. */}
                            {!incluida && (
                              <button
                                type="button"
                                onClick={() => (editando ? reset() : handleEditar(idx))}
                                className="text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200"
                                title={editando ? "Cancelar edición" : "Editar línea"}
                              >
                                <PencilSquareIcon className="h-4 w-4" />
                              </button>
                            )}
                            <button
                              type="button"
                              className="text-red-500 hover:text-red-700 text-xs font-medium"
                              onClick={() => handleQuitar(idx)}
                              title={
                                l.grupoId
                                  ? "Elimina la renta y lo que trae incluido"
                                  : undefined
                              }
                            >
                              Eliminar
                            </button>
                          </span>
                        </td>
                      )}
                    </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="font-semibold" style={{ backgroundColor: ACCENT + "0d" }}>
                <td
                  className="px-3 py-2 text-right text-xs uppercase tracking-wide text-zinc-500"
                  colSpan={3}
                >
                  {ivaEnabled ? "Subtotal" : "Total desglose"}
                </td>
                <td className="px-3 py-2 text-zinc-900 dark:text-white">{fmtMoney(subtotal)}</td>
                {!readOnly && <td />}
              </tr>
              {ivaEnabled && (
                <>
                  <tr style={{ backgroundColor: ACCENT + "0d" }}>
                    <td
                      className="px-3 py-2 text-right text-xs uppercase tracking-wide text-zinc-500"
                      colSpan={3}
                    >
                      IVA (16%)
                    </td>
                    <td className="px-3 py-2 text-zinc-700 dark:text-zinc-300">
                      {fmtMoney(ivaAmount, 2)}
                    </td>
                    {!readOnly && <td />}
                  </tr>
                  <tr className="font-semibold" style={{ backgroundColor: ACCENT + "0d" }}>
                    <td
                      className="px-3 py-2 text-right text-xs uppercase tracking-wide text-zinc-500"
                      colSpan={3}
                    >
                      Total con IVA
                    </td>
                    <td className="px-3 py-2 text-zinc-900 dark:text-white">
                      {fmtMoney(totalConIva, 2)}
                    </td>
                    {!readOnly && <td />}
                  </tr>
                </>
              )}
            </tfoot>
          </table>
        </div>
      )}

      {/* Redondeo de centavos */}
      {!readOnly &&
        lineas.length > 0 &&
        totalFinal > 0 &&
        Math.abs(totalFinal - Math.round(totalFinal)) >= 0.005 && (
          <div className="mb-3 flex flex-wrap items-center gap-2 text-xs text-zinc-500">
            <span>El total quedó con centavos.</span>
            <button
              type="button"
              onClick={() => handleRedondear("abajo")}
              className="font-semibold text-blue-600 hover:underline"
            >
              Redondear a {fmtMoney(Math.floor(totalFinal))}
            </button>
            <span>·</span>
            <button
              type="button"
              onClick={() => handleRedondear("arriba")}
              className="font-semibold text-blue-600 hover:underline"
            >
              a {fmtMoney(Math.ceil(totalFinal))}
            </button>
          </div>
        )}

      {/* Desglose vs precio acordado */}
      {precioAcordado != null && lineas.length > 0 && (
        Math.abs(totalFinal - Number(precioAcordado || 0)) >= 0.01 ? (
          <div className="mb-3 text-sm text-red-600">
            El total{ivaEnabled ? " con IVA" : " del desglose"} ({fmtMoney(totalFinal, ivaEnabled ? 2 : null)}) no
            coincide con el precio acordado ({fmtMoney(precioAcordado)}). Los saldos y cobros se calculan
            con el total de la cotización.
          </div>
        ) : (
          <div className="mb-3 text-sm text-green-600">
            El total{ivaEnabled ? " con IVA" : " del desglose"} ({fmtMoney(totalFinal, ivaEnabled ? 2 : null)}) coincide
            con el precio acordado ({fmtMoney(precioAcordado)}).
          </div>
        )
      )}
      {lineas.length > 0 && totalDesglose(lineas, ivaEnabled) <= 0 && (
        <div className="mb-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-sm text-amber-800 dark:text-amber-300">
          La cotización tiene conceptos pero su total es $0 o negativo — revisa el desglose.
        </div>
      )}

      {/* Captura de línea. Tarjeta con encabezado propio, como la tabla del
          desglose, en vez de un bloque gris sin identidad: el formulario es la
          otra mitad de esta pantalla y tiene que leerse como una pieza, no como
          un relleno. "Crear servicio nuevo" sube al encabezado porque es una
          acción de la sección, no del campo Concepto. */}
      {!readOnly && (
        <div className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/40">
          <div
            className="flex flex-wrap items-center gap-2 px-4 py-2.5 rounded-t-xl border-b border-zinc-200 dark:border-zinc-700/60"
            style={{ backgroundColor: ACCENT + "0d" }}
          >
            <span className="text-sm font-semibold text-zinc-700 dark:text-zinc-200">
              {editandoIdx != null ? "Editar concepto" : "Agregar concepto"}
            </span>
            {puedeCrearServicio && (
              <button
                type="button"
                onClick={() => setShowCrearServicio(true)}
                className="ml-auto text-xs font-semibold hover:underline"
                style={{ color: ACCENT }}
              >
                Crear servicio nuevo
              </button>
            )}
          </div>

          <div className="p-4">
            <div className="space-y-3">
            {/* Concepto: la decisión principal, a todo el ancho. El panel del
                picker es ancho y en media columna quedaba apretado. */}
            <div>
              <label className="block text-xs font-medium text-zinc-500 mb-1">Concepto</label>
              {conceptosLoading ? (
                <div className="text-zinc-400 italic text-sm">Cargando...</div>
              ) : (
                <ConceptoPicker
                  conceptos={conceptos || []}
                  proveedores={proveedoresDelPicker}
                  categoriaNombrePorId={categoriaNombrePorId}
                  value={valorListbox}
                  onChange={elegirConcepto}
                  opcionesExtra={[
                    { value: OPCION_DESCUENTO, label: "Descuento…" },
                    { value: OPCION_LIBRE, label: "Otro concepto…" },
                  ]}
                />
              )}
              {form.modo === "libre" && (
                <input
                  type="text"
                  value={form.concept}
                  onChange={(e) => set({ concept: e.target.value })}
                  placeholder="Nombre del concepto"
                  className={`${inputClass} mt-2`}
                  autoFocus
                />
              )}
              {form.modo === "descuento" && !form.conceptoId && (
                <input
                  type="text"
                  value={form.concept}
                  onChange={(e) => set({ concept: e.target.value })}
                  placeholder="Nombre del descuento"
                  className={`${inputClass} mt-2`}
                />
              )}
            </div>

            {/* Los campos que dependen del concepto elegido, en una sola fila:
                cada modo muestra los suyos, así que van en un flex con anchos
                propios y no en una rejilla de dos columnas donde "Cantidad"
                se llevaba media pantalla para un "1". */}
            <div className="flex flex-wrap items-start gap-3">
            {/* Variante (solo si el servicio tiene o se puede crear una) */}
            {form.modo === "servicio" && item && (
              <div className="w-full sm:w-56">
                <label className="block text-xs font-medium text-zinc-500 mb-1">Variante</label>
                <select
                  value={form.varianteId}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === "__nueva__") {
                      setNuevaVariante({ ...NUEVA_VARIANTE_VACIA });
                    } else {
                      setNuevaVariante(null);
                    }
                    set({ varianteId: v });
                  }}
                  className={inputClass}
                >
                  <option value="">
                    {variantes.length ? "Sin variante (precio base)" : "Sin variante"}
                  </option>
                  {variantes.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.nombre}
                    </option>
                  ))}
                  <option value="__nueva__">+ Nueva variante…</option>
                </select>
                {variante && (
                  <p className="mt-1 text-xs text-zinc-500">
                    {variante.nombre}: precio propio de esta variante.
                  </p>
                )}
              </div>
            )}

            {/* Cantidad y precio */}
            {form.modo === "servicio" && item && (
              <>
                <div className="w-28">
                  <label className="block text-xs font-medium text-zinc-500 mb-1">
                    Cantidad ({plural(2, unidad)})
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="any"
                    inputMode="decimal"
                    value={form.cantidad}
                    onChange={(e) => set({ cantidad: e.target.value })}
                    className={`${inputClass} ${
                      excedeCapacidad ? "border-red-400 focus:ring-red-400" : ""
                    }`}
                    placeholder="1"
                  />
                  {capacidadItem != null && (
                    <p
                      className={`mt-1 text-[11px] leading-tight ${
                        excedeCapacidad ? "font-semibold text-red-600" : "text-zinc-400"
                      }`}
                    >
                      {excedeCapacidad
                        ? `Solo tienes ${capacidadItem}`
                        : `Tienes ${capacidadItem}`}
                    </p>
                  )}
                </div>
                <div className="w-full sm:w-64">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <label className="block text-xs font-medium text-zinc-500">
                      {form.ajustarPrecio ? `Precio por ${unidad} (ajustado)` : "Precio"}
                    </label>
                    <button
                      type="button"
                      onClick={() =>
                        set({
                          ajustarPrecio: !form.ajustarPrecio,
                          precioManual: !form.ajustarPrecio
                            ? String(precioActivo || "")
                            : "",
                        })
                      }
                      className="text-[11px] font-medium text-zinc-500 hover:text-zinc-700"
                    >
                      {form.ajustarPrecio ? "Usar precio del catálogo" : "Ajustar precio"}
                    </button>
                  </div>
                  {form.ajustarPrecio ? (
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none text-sm">$</span>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={fmtMXNInput(form.precioManual)}
                        onChange={(e) => set({ precioManual: normalizeMoneyInput(e.target.value) })}
                        className={`${inputClass} pl-7`}
                        placeholder="0"
                      />
                    </div>
                  ) : (
                    <div className="rounded-lg border border-zinc-200 dark:border-zinc-700/60 bg-zinc-50 dark:bg-zinc-800 px-3 py-1.5 text-sm">
                      {!(precioActivo > 0) ? (
                        <span className="text-zinc-400">Sin precio al cliente.</span>
                      ) : preview ? (
                        <>
                          <div className="text-base font-semibold leading-tight text-zinc-900 dark:text-white">
                            {fmtMoney(preview.total)}
                          </div>
                          {/* La cuenta debajo, no pegada al total: juntas salían
                              como "$1,500(1 × $1,500)" y parecía un error. */}
                          <div className="text-[11px] leading-tight text-zinc-400">
                            {cantidad > 0
                              ? `${cantidad.toLocaleString("es-MX", { maximumFractionDigits: 2 })} × ${fmtMoney(precioActivo)}`
                              : `${fmtMoney(precioActivo)} por ${unidad}`}
                          </div>
                        </>
                      ) : (
                        <span className="text-zinc-400">Ingresa la cantidad.</span>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}

            {form.modo === "libre" && (
              <>
                <div className="w-28">
                  <label className="block text-xs font-medium text-zinc-500 mb-1">Cantidad</label>
                  <input
                    type="number"
                    min="0"
                    step="any"
                    inputMode="decimal"
                    value={form.cantidad}
                    onChange={(e) => set({ cantidad: e.target.value })}
                    className={inputClass}
                    placeholder="1"
                  />
                </div>
                <div className="w-full sm:w-64">
                  <label className="block text-xs font-medium text-zinc-500 mb-1">Precio unitario</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none text-sm">$</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={fmtMXNInput(form.precioManual)}
                      onChange={(e) => set({ precioManual: normalizeMoneyInput(e.target.value) })}
                      className={`${inputClass} pl-7`}
                      placeholder="0"
                    />
                  </div>
                </div>
              </>
            )}

            {form.modo === "descuento" && (
              <div className="w-full sm:w-72">
                {/* El selector $/% vive DENTRO del campo, no junto al label.
                    Iba en la misma fila que el label y, al ser más alto que
                    una línea de texto, empujaba el input hacia abajo: en la
                    rejilla de dos columnas quedaba desalineado contra
                    "Concepto", que sí lleva un label de una sola línea. */}
                <label className="block text-xs font-medium text-zinc-500 mb-1">
                  {form.descuentoModo === "pct" ? "Porcentaje del total" : "Monto a descontar"}
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none text-sm">
                    {form.descuentoModo === "pct" ? "%" : "$"}
                  </span>
                  {form.descuentoModo === "pct" ? (
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.5"
                      value={form.descuentoPct}
                      onChange={(e) => set({ descuentoPct: e.target.value })}
                      className={`${inputClass} pl-7 pr-20`}
                      placeholder="10"
                    />
                  ) : (
                    <input
                      type="text"
                      inputMode="decimal"
                      value={fmtMXNInput(form.precioManual)}
                      onChange={(e) => set({ precioManual: normalizeMoneyInput(e.target.value).replace("-", "") })}
                      className={`${inputClass} pl-7 pr-20`}
                      placeholder="0"
                    />
                  )}
                  <div className="absolute right-1.5 top-1/2 -translate-y-1/2 flex rounded-md border border-zinc-300 dark:border-zinc-600 overflow-hidden text-xs">
                    {[
                      { id: "monto", label: "$" },
                      { id: "pct", label: "%" },
                    ].map((m) => (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => set({ descuentoModo: m.id })}
                        title={m.id === "pct" ? "Porcentaje del total" : "Monto fijo"}
                        className={`px-2 py-0.5 font-semibold transition ${
                          form.descuentoModo === m.id
                            ? "text-white"
                            : "bg-white dark:bg-zinc-800 text-zinc-500 hover:bg-zinc-50"
                        }`}
                        style={form.descuentoModo === m.id ? { backgroundColor: ACCENT } : undefined}
                      >
                        {m.label}
                      </button>
                    ))}
                  </div>
                </div>
                {form.descuentoModo === "pct" && Number(form.descuentoPct) > 0 && subtotal > 0 && (
                  <p className="mt-1 text-xs text-zinc-500">
                    Descuenta {fmtMoney(Math.round(subtotal * Number(form.descuentoPct)) / 100, 2)} del total actual.
                  </p>
                )}
              </div>
            )}
            </div>

            {/* Lo que la renta trae: llega todo marcado y se desmarca lo que
                este evento no lleve. Cada marcado entra como renglón propio en
                $0, así que el cliente lee qué incluye su renta y el costo del
                proveedor de un incluido se sigue registrando solo. */}
            {form.modo === "servicio" && itemEsRenta && incluidosDelItem.length > 0 && (
              <div className="sm:col-span-2 rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800/60 p-3">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <div className="text-xs font-medium text-zinc-500">
                    Qué incluye · {incluidosMarcados.length} de {incluidosDelItem.length}
                  </div>
                  <div className="ml-auto flex items-center gap-2 text-xs">
                    <button
                      type="button"
                      onClick={() => marcarTodos(true)}
                      className="font-semibold hover:underline"
                      style={{ color: ACCENT }}
                    >
                      Todo
                    </button>
                    <span className="text-zinc-300">·</span>
                    <button
                      type="button"
                      onClick={() => marcarTodos(false)}
                      className="font-semibold text-zinc-500 hover:underline"
                    >
                      Ninguno
                    </button>
                  </div>
                </div>
                <ul className="divide-y divide-zinc-100 dark:divide-zinc-700/40">
                  {incluidosDelItem.map(({ concepto, conceptoId, cantidadConfigurada }) => {
                    const estado = form.incluidos?.[conceptoId] || {};
                    const marcado = !!estado.sel;
                    const pid = proveedorDe(concepto);
                    const prov = pid
                      ? (proveedores || []).find((x) => x.id === pid)?.nombre ||
                        concepto.proveedorNombre
                      : null;
                    return (
                      <li key={conceptoId} className="flex items-center gap-2 py-2">
                        <label className="flex min-w-0 flex-1 items-start gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={marcado}
                            onChange={(e) => marcarIncluido(conceptoId, e.target.checked)}
                            className="mt-1"
                          />
                          <span className="min-w-0">
                            <span
                              className={`block text-sm truncate ${
                                marcado
                                  ? "text-zinc-900 dark:text-white"
                                  : "text-zinc-400 line-through"
                              }`}
                            >
                              {concepto.nombre}
                            </span>
                            {/* Que el incluido lo traiga un proveedor no es un
                                detalle: al quedar marcado, su costo se registra
                                en el evento igual que si fuera línea propia. */}
                            {prov && (
                              <span className="block text-xs text-zinc-500">Lo da {prov}</span>
                            )}
                          </span>
                        </label>
                        <input
                          type="number"
                          min="0"
                          step="any"
                          disabled={!marcado}
                          value={estado.cantidad ?? String(cantidadConfigurada)}
                          onChange={(e) => cantidadIncluido(conceptoId, e.target.value)}
                          className="w-20 rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-2 py-1 text-sm disabled:opacity-40"
                          title={`Cuántas ${plural(2, unidadDe(concepto))} van en este evento`}
                        />
                        <span className="w-16 text-xs text-zinc-400 truncate">
                          {plural(Number(estado.cantidad) || cantidadConfigurada, unidadDe(concepto))}
                        </span>
                      </li>
                    );
                  })}
                </ul>
                {incluidosMarcados.length < incluidosDelItem.length && (
                  <p className="mt-2 text-xs text-amber-600">
                    Lo desmarcado no aparece en la cotización ni genera costo. El
                    precio de la renta no cambia: si le vas a compensar al cliente,
                    agrega un descuento.
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Variante nueva, inline: queda en el catálogo al guardarla */}
          {form.modo === "servicio" && item && nuevaVariante && (
            <div className="mt-3 rounded-lg border border-dashed border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800/60 p-3 space-y-3">
              <div className="text-xs font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                Nueva variante de {item.nombre}
              </div>
              <p className="text-xs text-zinc-500">
                Se guarda en el catálogo: la próxima vez ya estará disponible.
              </p>
              <input
                type="text"
                value={nuevaVariante.nombre}
                onChange={(e) => setNuevaVariante((v) => ({ ...v, nombre: e.target.value }))}
                placeholder="Nombre (12 horas, Sábado, Con luces…)"
                className={`${inputClass} max-w-md`}
                autoFocus
              />
              <PreviewVariante
                nombreServicio={item.nombre}
                nombreVariante={nuevaVariante.nombre}
              />
              {/* Dejar un campo vacío NO es "sin precio" ni "sin costo": la
                  variante HEREDA el del servicio (nuevaVariante en
                  utils/catalogo.js guarda null = hereda). Decirle "opcional"
                  y poner $0 de placeholder escondía eso, y desde que la
                  cotización registra costos sola (utils/cotizacionProveedores)
                  ese número ya no solo pinta un margen: se escribe como
                  egreso. Así que se enseña lo que va a heredar, con su cifra.
                  Obligarlo sería peor: casi toda variante cambia UNA
                  dimensión, y recapturar la tarifa en cada una significa ir
                  variante por variante cada vez que el proveedor la sube. */}
              <div className={`grid grid-cols-1 ${proveedorDelItem ? "sm:grid-cols-2" : ""} gap-3`}>
                <div>
                  <label className="block text-xs font-medium text-zinc-500 mb-1">
                    Precio al cliente
                    {precioBaseItem > 0 && !(Number(normalizeMoneyInput(nuevaVariante.precio)) > 0) && (
                      <span className="font-normal text-zinc-400">
                        {" "}· hereda {fmtMoney(precioBaseItem)}
                      </span>
                    )}
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={fmtMXNInput(nuevaVariante.precio)}
                    onChange={(e) =>
                      setNuevaVariante((v) => ({ ...v, precio: normalizeMoneyInput(e.target.value) }))
                    }
                    placeholder={precioBaseItem > 0 ? fmtMoney(precioBaseItem) : "$0"}
                    className={inputClass}
                  />
                </div>
                {proveedorDelItem && (
                  <div>
                    <label className="block text-xs font-medium text-zinc-500 mb-1">
                      {`Costo de ${proveedorDelItem.nombre}`}
                      {!(Number(normalizeMoneyInput(nuevaVariante.costoProveedor)) > 0) && (
                        <span className="font-normal text-zinc-400">
                          {costoBaseItem > 0
                            ? ` · hereda ${fmtMoney(costoBaseItem)}`
                            : " · el servicio no tiene tarifa"}
                        </span>
                      )}
                    </label>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={fmtMXNInput(nuevaVariante.costoProveedor)}
                      onChange={(e) =>
                        setNuevaVariante((v) => ({
                          ...v,
                          costoProveedor: normalizeMoneyInput(e.target.value),
                        }))
                      }
                      placeholder={costoBaseItem > 0 ? fmtMoney(costoBaseItem) : "$0"}
                      className={inputClass}
                    />
                  </div>
                )}
              </div>

              {/* El margen que va a quedar, con lo capturado y lo heredado
                  ya resuelto. Es la única forma de notar en el momento que
                  subiste el precio al cliente y dejaste el costo del base. */}
              {margenVarianteNueva && (
                <div
                  className={`rounded-lg px-3 py-2 text-xs ${
                    margenVarianteNueva.margen < 0
                      ? "bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-300"
                      : "bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300"
                  }`}
                >
                  Cobras {fmtMoney(margenVarianteNueva.precio)} y te cuesta{" "}
                  {fmtMoney(margenVarianteNueva.costo)}
                  {margenVarianteNueva.costoHeredado && " (heredado del servicio)"} ·{" "}
                  <strong>
                    margen {fmtMoney(margenVarianteNueva.margen)}
                    {margenVarianteNueva.pct !== null
                      ? ` (${(margenVarianteNueva.pct * 100).toFixed(0)}%)`
                      : ""}
                  </strong>
                </div>
              )}
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleGuardarVariante}
                  disabled={guardandoVariante}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white shadow-sm transition hover:opacity-90 disabled:opacity-50"
                  style={{ backgroundColor: ACCENT }}
                >
                  {guardandoVariante ? "Guardando…" : "Guardar variante en el catálogo"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setNuevaVariante(null);
                    set({ varianteId: "" });
                  }}
                  className="text-xs text-zinc-500 hover:text-zinc-700"
                >
                  Cancelar
                </button>
              </div>
            </div>
          )}

          {/* La fila de acciones cierra el formulario con una línea propia: sin
              ella los botones flotaban sobre el fondo gris de la tarjeta y el
              secundario, en texto plano, no se leía como un botón — aparecía y
              se perdía. Va como secundario de verdad, del mismo alto y radio
              que el primario, para que los dos se lean como un par. */}
          <div className="mt-4 -mx-4 -mb-4 px-4 py-3 rounded-b-xl border-t border-zinc-200 dark:border-zinc-700/60 flex items-center justify-end gap-2 bg-zinc-50/60 dark:bg-zinc-800/30">
            {(form.modo || editandoIdx != null) && (
              <button
                type="button"
                onClick={reset}
                className="rounded-lg border border-zinc-300 bg-white px-4 py-2 text-sm font-medium text-zinc-700 shadow-sm transition hover:bg-zinc-50 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-200 dark:hover:bg-zinc-700/60"
              >
                {editandoIdx != null ? "Cancelar edición" : "Cancelar"}
              </button>
            )}
            <button
              type="button"
              onClick={handleRegistrar}
              className="inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm transition"
              style={{ backgroundColor: ACCENT }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#164026")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = ACCENT)}
            >
              {editandoIdx != null ? "Guardar cambios" : "Registrar concepto"}
            </button>
          </div>
          </div>
        </div>
      )}

      {puedeCrearServicio && (
        <ServicioCreateDialog
          open={showCrearServicio}
          onClose={() => setShowCrearServicio(false)}
          onCreated={(id) => {
            // El snapshot del catálogo lo trae enseguida; mientras, queda
            // seleccionado por id y la vista previa aparece al llegar.
            setForm({ ...FORM_VACIO, modo: "servicio", conceptoId: id, cantidad: "1" });
          }}
        />
      )}
    </div>
  );
}
